import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'expo-router';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Keyboard,
  Platform,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { AuthTokenResponse } from '@/api/customers/use-create-customer';
import { useCustomerStartOtp } from '@/api/customers/use-customer-start-otp';
import { useCustomerVerifyOtp } from '@/api/customers/use-customer-verify-otp';
import {
  type StartOtpResponse,
  useStartOtp,
} from '@/api/customers/use-start-otp';
import { useVerifyOtp } from '@/api/customers/use-verify-otp';
import type { InputOtpProps } from '@/components/input-phone';
import { signIn } from '@/core';
import { getToken } from '@/core/auth/utils';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Eon } from '@/ui/icons/eon';
import { Loader } from '@/ui/loader';

import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

const OTP_LENGTH = 6;

export type InputOtpProps = {
  otp: string;
};

const confirmOtpFormSchema = z.object({
  code: z.string().length(OTP_LENGTH),
});

export type ConfirmOtpFormType = z.infer<typeof confirmOtpFormSchema>;

export type ConfirmOtpFormProps = {
  phone: string;
  otp: string;
};

interface ConfirmOtpProps {
  phone: string;
  onBack: () => void;
  onDone: (setupComplete: boolean) => void;
}

// TODO: https://github.com/retyui/react-native-confirmation-code-field
export default function ConfirmOtp({
  onBack,
  onDone,
  phone,
}: ConfirmOtpProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [resentCode, setResentCode] = React.useState<boolean>(false);
  const [resendCodeTimer, setResendCodeTimer] = React.useState(60);
  const router = useRouter();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Verify Otp',
    });
  }, []);

  const token = getToken();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid },
  } = useForm<ConfirmOtpFormType>({
    resolver: zodResolver(confirmOtpFormSchema),
  });

  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useVerifyOtp();

  const { mutate: startOtp, isPending: isStartingOtp } = useStartOtp();

  const { mutate: startCustomerOtp, isPending: isStartingCustomerOtp } =
    useCustomerStartOtp();

  const { mutate: verifyCustomerOtp, isPending: isStartingCustomerVerifyOtp } =
    useCustomerVerifyOtp();

  useEffect(() => {
    if (resendCodeTimer > 0) {
      const countdown = setTimeout(() => {
        setResendCodeTimer(resendCodeTimer - 1);
      }, 1000);

      return () => clearTimeout(countdown);
    } else {
      setResentCode(false);
    }
  }, [resendCodeTimer]);

  const handleOtpSubmit = (data: InputOtpProps) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Verify Otp Code',
    });

    if (token) {
      verifyCustomerOtp(
        {
          phone,
          otp: `${data.code}`,
        },
        {
          onSuccess: () => {
            onDone(true);
          },
          onError: (e) => {
            showErrorMessage(e.message);
          },
        }
      );
    } else {
      verifyOtp(
        {
          phone,
          otp: `${data.code}`,
        },
        {
          onSuccess: (respData: AuthTokenResponse) => {
            Toast.show({
              text1: 'Signed in successfully',
              type: 'success',
            });
            signIn(respData.data.token);

            onDone(respData.data.setupComplete);
          },
          onError: (e) => {
            showErrorMessage(e.message);
          },
        }
      );
    }
  };

  const handleResendOtp = () => {
    if (token) {
      startCustomerOtp(
        {
          phone,
        },
        {
          onSuccess: (_: StartOtpResponse) => {
            setResendCodeTimer(60);
            setResentCode(true);
          },
          onError: (e) => {
            showErrorMessage(e.message);
          },
        }
      );
    } else {
      startOtp(
        {
          phone,
        },
        {
          onSuccess: (_: StartOtpResponse) => {
            setResendCodeTimer(60);
            setResentCode(true);
          },
          onError: (e) => {
            showErrorMessage(e.message);
          },
        }
      );
    }
  };

  if (
    isStartingOtp ||
    isStartingCustomerOtp ||
    isVerifyingOtp ||
    isStartingCustomerVerifyOtp
  ) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <TouchableWithoutFeedback
      onPress={() => {
        if (Platform.OS !== 'web') {
          Keyboard.dismiss();
        }
      }}
    >
      <View
        style={{
          flexGrow: 1,
          borderRadius: 16,
          backgroundColor: theme.colors.background_2,
        }}
      >
        <View style={styles.container}>
          <View style={styles.formContainer}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                onBack();
              }}
            />

            {Platform.OS !== 'web' && <Eon width="48" height="18" />}

            <Text tx="confirmOtp.subtitle" type="title1" />

            <Text
              tx="confirmOtp.body"
              type="bodyRegular"
              values={{ phone: phone }}
            />

            <View style={styles.inputsContainer}>
              <ControlledInput
                control={control}
                name="code"
                label="Code"
                keyboardType="number-pad"
                textContentType="oneTimeCode"
                onChangeText={(text) => {
                  setValue('code', text);
                  trigger('code');
                }}
              />
            </View>

            <View style={styles.buttonContainer}>
              <Button
                tx="confirmOtp.verifyCode"
                type="primary"
                onPress={handleSubmit(handleOtpSubmit)}
                disabled={!isValid}
              />
            </View>

            <View style={styles.resendContainer}>
              {resentCode ? (
                <Text
                  tx="confirmOtp.resentCode"
                  type="caption1"
                  values={{ timer: resendCodeTimer }}
                />
              ) : (
                <Text tx="confirmOtp.noCode" type="caption1" />
              )}

              <Button
                tx="confirmOtp.resendCode"
                type="link"
                onPress={handleResendOtp}
                textStyle={{ fontSize: 12 }}
                style={{
                  width: 75,
                  marginLeft: 4,
                  paddingTop: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                }}
                disabled={resentCode && resendCodeTimer > 0}
              />
            </View>
          </View>
        </View>
      </View>
    </TouchableWithoutFeedback>
  );
}

const stylesheet = createStyleSheet(() => ({
  container: {
    flexGrow: 1,
    paddingTop: 16,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
    gap: 16,
    alignItems: 'center',
  },
  formContainer: {
    marginTop: 20,
    gap: 16,
  },
  inputsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  buttonContainer: {
    marginTop: 8,
    alignItems: 'center',
  },
  resendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
